import { Box, Flex, Tooltip } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import countryCodeToNameMapping from 'api/data/countryCodeToNameMapping'

import { FullPageError, LoadingAnimation } from 'components'

import useAxios from 'api/useAxios'

import { LayerT } from './Map/layers'
import { CladeDataT, DataT, FeaturesT } from './Map/utils/types'
import { subYears } from 'date-fns'
import _ from 'lodash'
import { MapRef } from 'react-map-gl'
import { recursiveCamelCase } from 'api/helper'

import MainFilterSection from './Filters'
import moment from 'moment'
import {
  DynamicFeatureOptions,
  getBotulismFeatures,
  getMpoxCollections,
} from './Map/utils'
import {
  fetchCountryPolygons,
  getQueryString,
  removeNullCases,
} from './helpers'
import MapSection from './Map'
import { Information } from '@carbon/icons-react'
import { batchAndThrottleRequests } from './requestHelper'
import RightSection from './RightSection'
import LeftSection from './LeftSection'
import AboutDataPopover from './AboutDataPopover'
import UpsellBanner from '../common/UpsellBanner'

export const DefaultStartDate = subYears(new Date(), 1)

export interface TopCountriesT {
  area: string
  country: string
  countryCode: string
  value: number
  allCases: DataT[]
}
export interface EpiTrendDataT {
  area: string
  country: string
  value: number
  allCases: DataT[]
}

export type DemoDataT = _.Dictionary<DataT[]>

export const Card = ({
  minW,
  w,
  h,
  children,
  info,
  overflow,
  allowExpand = false,
}: {
  minW?: any
  h?: string
  w?: string
  children: any
  info?: string
  allowExpand?: boolean
  overflow?: string
}) => {
  return (
    <Box
      border='1px solid'
      borderColor='gray3'
      h={h || '100%'}
      w={w || '100%'}
      minW={minW || '350px'}
      p='1rem'
      bg='white'
      rounded='md'
      overflow={overflow || 'hidden'}
      pos='relative'
    >
      {info ? (
        <Flex pos='absolute' top={4} right={4}>
          <Tooltip label={info}>
            <Information color='gray5' />
          </Tooltip>
        </Flex>
      ) : null}
      {children}
    </Box>
  )
}

export const CARD_WIDTH = '500px'
export const CARD_HEIGHT = '17.4vh'

export function getCountryName(
  countryCode: string,
  mapping: Record<string, string> = countryCodeToNameMapping
) {
  const code = countryCode?.toUpperCase()
  return mapping?.[code] || countryCode
}

export interface FiltersT {
  dataType: string
  dateRange: {
    start: string
    end: string
  }
  country: string
  nCountries: number
}

const DefaultFilters = {
  dataType: 'infections',
  dateRange: {
    start: moment().startOf('year').format('YYYY-MM-DD'),
    end: moment().format('YYYY-MM-DD'),
  },
  country: 'global',
  nCountries: 5,
}

const EpidemiologyDashboard = ({ disease = 'Mpox' }) => {
  const mapRef = useRef<MapRef>(null)

  const isMpox = disease === 'Mpox'

  const [filters, setFilters] = useState<FiltersT>(DefaultFilters)
  const [selectedLayer, setLayer] = useState<LayerT>('')
  const [features, setFeatures] = useState<FeaturesT>()
  const [legendType, setLegendType] = useState<string>('Cases')
  const [mapLayers, setMapLayers] = useState<string[]>()

  const [countriesList, setCountryList] = useState<string[]>()

  const axios = useAxios()

  const {
    data,
    error: dataError,
    isLoading,
  } = useQuery(
    [filters.dataType, filters.dateRange, filters.country],
    async () => {
      const { start, end } = filters.dateRange
      const formattedStartDate = moment(start).format('YYYY-MM-DD')
      const formattedEndDate = moment(end).format('YYYY-MM-DD')

      const queryString = getQueryString(
        filters.dataType,
        formattedStartDate,
        formattedEndDate,
        'global',
        disease
      )

      const [resStandard, resClade, resCountryPolys] = await Promise.all([
        axios.get(queryString.standard),
        isMpox ? axios.get(queryString.clade) : Promise.resolve({ data: [] }),
        fetchCountryPolygons(),
      ])

      const standard = removeNullCases(recursiveCamelCase(resStandard.data))
      const clade = recursiveCamelCase(resClade?.data) as CladeDataT

      const isGlobal = filters.country === 'global'

      const correctClade = clade.filter(
        (c: any) =>
          c.clade === 'Clade Ia' ||
          c.clade === 'Clade Ib' ||
          c.clade === 'Clade I'
      )

      const countryClades = isGlobal
        ? correctClade
        : correctClade.filter((c: any) => c.areaAlpha3 === filters.country)

      const dateClades = countryClades.filter((clade) => {
        const isValid = moment(
          clade.date || clade.dateSubmitted,
          'YYYY-MM-DD'
        ).isBetween(moment('2024-01-01'), moment(end), undefined, '[]')
        return isValid
      })

      const finalCladeList = dateClades

      const uniqueAreas = [
        ...new Set(
          finalCladeList
            .filter((x: any) => !!x.areaLongitude)
            .map((item: any) => item.areaId)
        ),
      ] as string[]

      const areaPolygons =
        !isGlobal &&
        ((await batchAndThrottleRequests(axios, uniqueAreas)) as any)

      function findAreaLevelById(areaId: string) {
        return (
          // eslint-disable-next-line eqeqeq
          finalCladeList.find((x: any) => x.areaId == areaId)?.areaAdminLevel ??
          0
        )
      }

      if (!isGlobal) {
        areaPolygons.features = [
          ...areaPolygons.features,
          ...resCountryPolys.features,
        ]
          .map((poly: any) => ({
            ...poly,
            adminLevel: findAreaLevelById(poly.properties.areaId),
          }))
          .sort((a: any, b: any) => a.adminLevel - b.adminLevel)
      }

      return {
        standard: standard as DataT[],
        clade: finalCladeList,
        countryPolygons: isGlobal ? resCountryPolys : areaPolygons,
      }
    }
  )

  function handleLegendClick(newLayer: LayerT) {
    setLayer(newLayer === selectedLayer ? '' : newLayer)
  }

  useEffect(() => {
    if (data && data?.countryPolygons) {
      const countryFilter =
        filters.country !== 'global' ? filters.country : undefined

      const casesLegendType = isMpox || legendType === 'Cases'

      const getFeatures = isMpox ? getMpoxCollections : getBotulismFeatures

      const dataStandard = data.standard.filter((item) => {
        if (isMpox) return true
        if (legendType === 'Cases' && item.disease === 11976) return true
        if (legendType === 'Types' && item.disease !== 11976) return true
        return false
      })

      const _uniqueDiseasesTypes = casesLegendType
        ? ['confirmed', 'suspected', 'notSpecified']
        : Object.keys(_.groupBy(dataStandard, 'diseaseName')).sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
          )

      const diseaseNameConfig: DynamicFeatureOptions<DataT> | undefined =
        casesLegendType
          ? undefined
          : {
              categories: _uniqueDiseasesTypes.map((diseaseName) => ({
                key: diseaseName,
                predicate: (d: any) => d.diseaseName === diseaseName,
              })),
              valueKey: 'value', // Change if your metric lives under a different column
            }

      const _features = getFeatures(
        dataStandard,
        data.clade,
        data.countryPolygons,
        filters.dateRange.end,
        countryFilter,
        diseaseNameConfig
      ) as FeaturesT

      const uniqueLayers = Object.keys(_features).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      )

      const countries = _.groupBy(data.standard, (item) => item.areaAlpha3)

      setMapLayers(uniqueLayers)
      setFeatures(_features)
      setCountryList(Object.keys(countries))
    }
  }, [
    isMpox,
    data,
    filters.dateRange,
    filters.country,
    filters.dataType,
    disease,
    legendType,
  ])

  function selectCountry(countryId: string) {
    let _country = 'global'
    let fly
    if (!!countryId && countryId !== filters.country) {
      fly = countryId
      _country = countryId
    }
    flyTo(fly)
    setFilters((fil) => ({ ...fil, country: _country }))
  }

  function flyTo(countryId?: string) {
    let coords = [0, 10] as [number, number]
    if (countryId && countryId !== 'global') {
      const selectedCountry = data?.countryPolygons?.features.find(
        (feature: any) => feature.id === countryId
      )
      coords = selectedCountry?.properties?.coords
    }

    mapRef.current?.flyTo({
      center: coords,
      essential: true,
      zoom: countryId && countryId !== 'global' ? 2 : 0,
      speed: 0.5,
      curve: 1,
    })
  }

  if (dataError) {
    console.error({ dataError })
    return <FullPageError />
  }

  const countryData = data?.standard ?? undefined

  return (
    <Flex flexDir={'column'} gap='1rem'>
      <Flex gap='1rem' alignItems={'center'}>
        <AboutDataPopover disease={disease} />
        <MainFilterSection
          countries={countriesList || []}
          handleFilters={setFilters}
          handleCountry={selectCountry}
          filters={filters}
          disease={disease}
          isSingleType='Infections'
        />
      </Flex>
      <Flex
        gap={2}
        h={['auto', 'auto', 'auto', 'auto', '75vh']}
        flexWrap={['wrap', 'wrap', 'wrap', 'wrap', 'nowrap']}
      >
        <LeftSection filters={filters} disease={disease} />
        <Flex
          id='divToPrint'
          w='100%'
          gap={2}
          h={['auto', 'auto', 'auto', 'auto', '75vh']}
          flexWrap={['wrap', 'wrap', 'wrap', 'wrap', 'nowrap']}
        >
          {!features || isLoading ? (
            <Card w='100%'>
              <Flex
                justifyContent={'center'}
                alignItems={'center'}
                h='100%'
                w='100%'
              >
                <LoadingAnimation />
              </Flex>
            </Card>
          ) : (
            <MapSection
              mapRef={mapRef}
              features={features}
              selectedLayer={selectedLayer}
              handleLegendClick={handleLegendClick}
              selectCountry={selectCountry}
              width='100%'
              disease={disease}
              mapLayers={mapLayers}
              setLegendType={setLegendType}
              legendType={legendType}
              exportTitle='Epi Map Export'
            />
          )}
          <RightSection
            filters={filters}
            dataType={filters.dataType}
            selectCountry={selectCountry}
            disease={disease}
            countryData={countryData}
            layers={{
              mapLayers,
              selectedLayer,
              handleLegendClick,
            }}
          />
        </Flex>
      </Flex>
      {isMpox && <UpsellBanner />}
    </Flex>
  )
}

export default EpidemiologyDashboard
